<template>
  <div
    class="testimonial"
    :class="{
      'testimonial--transition-animated': hasTransitionAnimation,
      'testimonial--active-animated': hasActiveAnimation,
    }"
  >
    <button
      v-show="hasNavigation"
      class="testimonial__prev"
      data-track-as="testimonial.previous"
      :aria-controls="id"
      :aria-label="$t('previous')"
      @click="onPrevious"
    >
      <dotcom-icon
        class="testimonial__icon"
        art="light"
        name="chevron-right"
        color="--c-primary-neutral-1"
        rotation="180"
      />
    </button>
    <div ref="listWrapper" class="testimonial__list-wrapper">
      <ul
        :id="id"
        ref="list"
        class="testimonial__list"
        :style="`transform: translateX(-${offset + dragOffset}px);`"
      >
        <li
          v-for="(card, i) of testimonialsSlides"
          :key="i"
          :class="{
            ['testimonial__card--active']:
              i === activeCardIndexAbsoluteZeroBased,
          }"
          class="testimonial__card"
          :data-track-as="cardTracking(i)"
          role="group"
          :aria-roledescription="$t('testimonial')"
          :aria-label="
            $t('cardCount', { current: i + 1, all: testimonials.length })
          "
          @click="goToCard(i)"
        >
          <dotcom-card
            class="testimonial__card-item"
            :testimonial="card"
            :is-active="i === activeCardIndexAbsoluteZeroBased"
            :is-animated="hasTransitionAnimation"
            :is-active-animated="hasActiveAnimation"
            :is-visible="isCardVisible(i)"
          />
        </li>
      </ul>
    </div>
    <button
      v-show="hasNavigation"
      :aria-controls="id"
      :aria-label="$t('next')"
      class="testimonial__next"
      data-track-as="testimonial.next"
      @click="onNext"
    >
      <dotcom-icon
        art="light"
        name="chevron-right"
        class="testimonial__icon"
        color="--c-primary-neutral-1"
      />
    </button>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'

import DotcomCard from './components/Card'

export default {
  name: 'DotcomTestimonial',
  components: {
    DotcomCard,
    DotcomIcon: () => {
      return import(
        /* webpackChunkName: "dotcom-icon" */
        '@/modules/icon/Icon'
      )
    },
  },
  props: {
    testimonialList: {
      type: [Array, String],
      required: true,
    },
    id: {
      type: String,
      default() {
        return `testimonial-${Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, '')
          .substr(0, 5)}`
      },
    },
    autoplay: Boolean,
  },
  setup() {
    const language = window.Sanitas.language || 'de'
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data() {
    return {
      activeCardIndex: 2,
      testimonials: [],
      hasTransitionAnimation: false,
      hasActiveAnimation: false,
      isInTransition: false,
      offset: 0,
      dragOffset: 0,
      dragStartX: 0,
      isTouch: typeof window !== 'undefined' && 'ontouchstart' in window,
      localAutoplay: false,
    }
  },
  computed: {
    isMobile() {
      return !this.$mq['md!']
    },
    cloneLength() {
      // Without navigation no clones are needed
      return this.hasNavigation ? 3 : 0
    },
    hasNavigation() {
      return this.testimonials.length > (this.isMobile ? 1 : 3)
    },
    // All card slides including the clones at the start and end
    testimonialsSlides() {
      let slides = this.testimonials
      if (slides.length > (this.isMobile ? 1 : 3)) {
        // Add clones at the beginning and end for loop navigation
        const firstCards = slides.slice(0, this.cloneLength)
        const lastCards = slides.slice(this.cloneLength * -1)
        slides = lastCards.concat(slides, firstCards)
      } else if (slides.length < (this.isMobile ? 1 : 3)) {
        // Fill minimal number of slides with placeholders
        const missingSlides = (this.isMobile ? 1 : 3) - slides.length
        slides = slides.concat(Array(missingSlides).fill({}))
      }
      return slides
    },
    activeCardIndexAbsoluteZeroBased() {
      // activeIndex is a 1 based value for the cards without clones
      return this.activeCardIndex + this.cloneLength - 1
    },
  },
  async mounted() {
    this.testimonials =
      typeof this.testimonialList === 'string'
        ? JSON.parse(this.testimonialList)
        : this.testimonialList

    if (this.isMobile || this.testimonials.length < 2) {
      // For mobile show the first card initially
      // For desktop reset the initially active card to one if the default of 2 is not valid
      this.activeCardIndex = 1
    }

    await this.centerActiveCard()

    if (this.autoplay) {
      this.localAutoplay = true
      const autoplayInterval = window.setInterval(() => {
        if (!this.localAutoplay) {
          clearInterval(autoplayInterval)
          return
        }
        this.next()
      }, 5000)
    }

    window.addEventListener('resize', this.centerActiveCard, { passive: true })
    this.$refs.listWrapper.addEventListener(
      this.isTouch ? 'touchstart' : 'mousedown',
      this.onDragStart,
      { passive: true },
    )
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.centerActiveCard)
    this.$refs.listWrapper.removeEventListener(
      this.isTouch ? 'touchstart' : 'mousedown',
      this.onDragStart,
    )
  },
  methods: {
    async checkBorders() {
      // If a card near the start or end of the list is shown jump to the same card on the opposite side (without transition)
      if (
        this.activeCardIndex < 1 ||
        this.activeCardIndex > this.testimonials.length
      ) {
        this.hasTransitionAnimation = false
        this.hasActiveAnimation = false

        if (this.activeCardIndex < 1) {
          this.activeCardIndex = this.activeCardIndex + this.testimonials.length
        } else {
          this.activeCardIndex = this.activeCardIndex % this.testimonials.length
        }
      }
      await this.centerActiveCard()

      window.setTimeout(() => {
        this.hasTransitionAnimation = true
        this.hasActiveAnimation = true
        this.isInTransition = false
      }, 100)
    },
    onPrevious() {
      this.localAutoplay = false
      this.previous()
    },
    onNext() {
      this.localAutoplay = false
      this.next()
    },
    async previous() {
      if (this.isInTransition) {
        return
      }
      this.isInTransition = true
      this.hasTransitionAnimation = true
      this.hasActiveAnimation = true

      this.activeCardIndex--
      await this.centerActiveCard()

      // Wait for css transition to end
      window.setTimeout(() => {
        this.checkBorders()
      }, 300)
    },
    async next() {
      if (this.isInTransition) {
        return
      }
      this.isInTransition = true
      this.hasTransitionAnimation = true
      this.hasActiveAnimation = true

      this.activeCardIndex++
      await this.centerActiveCard()

      // Wait for css transition to end
      window.setTimeout(() => {
        this.checkBorders()
      }, 300)
    },
    async centerActiveCard() {
      await this.$nextTick()

      const cardWidth = this.$el
        .querySelectorAll('.testimonial__card')[0]
        .getBoundingClientRect().width
      const cardGapWidth = parseInt(
        getComputedStyle(this.$el).getPropertyValue(
          '--testimonial-card-gap-width',
        ),
        10,
      )
      // - 1 for desktop as there is an additional visible card left of the active one
      const numberOfInvisibleCardsLeft = this.isMobile
        ? this.activeCardIndexAbsoluteZeroBased
        : this.activeCardIndexAbsoluteZeroBased - 1

      this.offset = (cardWidth + cardGapWidth) * numberOfInvisibleCardsLeft
    },
    onDragStart(e) {
      if (!this.hasNavigation) {
        return
      }

      this.localAutoplay = false

      document.addEventListener(
        this.isTouch ? 'touchend' : 'mouseup',
        this.onDragEnd,
        true,
      )

      document.addEventListener(
        this.isTouch ? 'touchmove' : 'mousemove',
        this.onDrag,
        true,
      )

      this.hasTransitionAnimation = false
      this.dragStartX = this.isTouch ? e.touches[0].clientX : e.clientX
    },
    onDrag(e) {
      const eventPosX = this.isTouch ? e.touches[0].clientX : e.clientX
      const newOffsetX = this.dragStartX - eventPosX
      const elementWidth =
        this.$el.querySelector('.testimonial__card')?.clientWidth

      const resetDragValues = (newOffsetX, eventPosX) => {
        this.offset = this.offset + newOffsetX
        this.dragOffset = 0
        this.dragStartX = eventPosX
      }

      if (
        !!elementWidth &&
        newOffsetX > elementWidth &&
        this.activeCardIndex <= this.testimonials.length + 1
      ) {
        this.activeCardIndex++
        resetDragValues(newOffsetX, eventPosX)
      } else if (
        !!elementWidth &&
        newOffsetX < -elementWidth &&
        this.activeCardIndex > -1
      ) {
        this.activeCardIndex--
        resetDragValues(newOffsetX, eventPosX)
      } else {
        this.dragOffset = newOffsetX
      }
    },
    async onDragEnd(e) {
      this.hasTransitionAnimation = true
      const elementWidth =
        this.$el.querySelector('.testimonial__card')?.clientWidth

      // Go to next slide if dragend is more than half to the next slide on mobile. On desktop it feels unexpected.
      if (
        !!elementWidth &&
        Math.abs(this.dragOffset) > elementWidth / 2 &&
        this.isMobile
      ) {
        this.dragOffset > 0 ? await this.next() : await this.previous()
        this.dragOffset = 0
      } else {
        await this.checkBorders()
        this.dragOffset = 0
      }

      // clear events listeners
      document.removeEventListener(
        this.isTouch ? 'touchend' : 'mouseup',
        this.onDragEnd,
        true,
      )
      document.removeEventListener(
        this.isTouch ? 'touchmove' : 'mousemove',
        this.onDrag,
        true,
      )
    },
    isCardVisible(i) {
      const hasNoNavigation = !this.hasNavigation
      const isVisibleMobile = i === this.activeCardIndexAbsoluteZeroBased
      // For desktop show the active one and one on each side before and after
      const isVisibleDesktop =
        !this.isMobile &&
        [
          this.activeCardIndexAbsoluteZeroBased - 1,
          this.activeCardIndexAbsoluteZeroBased,
          this.activeCardIndexAbsoluteZeroBased + 1,
        ].includes(i)
      return hasNoNavigation || isVisibleMobile || isVisibleDesktop
    },
    async goToCard(index) {
      // Dragging in process
      if (this.isInTransition) return
      // Calculation as index is zero based and for all cards including cloned ones
      this.activeCardIndex = index - this.cloneLength + 1
      await this.centerActiveCard()
      await this.checkBorders()
    },
    cardTracking(index) {
      return index === this.activeCardIndexAbsoluteZeroBased
        ? 'testimonial.currentCard'
        : index > this.activeCardIndexAbsoluteZeroBased
        ? 'testimonial.nextCard'
        : 'testimonial.previousCard'
    },
  },
}
</script>

<style scoped lang="postcss">
.testimonial {
  --testimonial-card-factor: 0.95;
  --testimonial-card-active-factor: 1.1;
  --testimonial-card-gap-width: var(--spacing-2);

  position: relative;
  padding: var(--spacing-4) var(--spacing-6);
  margin: 0 calc(var(--container-offset-horizontal) * -1);
}

.testimonial__prev,
.testimonial__next {
  position: absolute;
  top: 0;
  bottom: 0;
  appearance: none;
  border: none;
  background: none;
  cursor: pointer;

  >>> .dotcom-icon {
    height: 28px;
    width: 18px;
  }
}

.testimonial__prev {
  left: 0;
  padding-left: 0;
}

.testimonial__next {
  right: 0;
  padding-right: 0;
}

.testimonial__list-wrapper {
  overflow: hidden;
  touch-action: none;
}

.testimonial__list {
  display: flex;
  align-items: center;
  gap: var(--testimonial-card-gap-width);
  list-style: none;
  min-height: 506px;
}

.testimonial--transition-animated .testimonial__list {
  transition: transform 300ms ease-in-out;
}

.testimonial--active-animated .testimonial__card {
  transition: flex-basis 300ms ease-in-out;
}

.testimonial__card {
  flex: 1 0 100%;
}

@media (--md) {
  .testimonial {
    --testimonial-card-width: calc((100% / 3 - var(--spacing-2) * 2 / 3));

    padding: var(--spacing-2) var(--spacing-6);
  }

  .testimonial__list {
    min-height: 530px;
  }

  .testimonial__card {
    flex-basis: calc(
      var(--testimonial-card-width) * var(--testimonial-card-factor)
    );
    perspective: 350px;
  }

  .testimonial__card--active {
    flex-basis: calc(
      var(--testimonial-card-width) * var(--testimonial-card-active-factor)
    );
  }
}

@media (--lg) {
  .testimonial {
    --testimonial-card-width: calc((100% / 3 - var(--spacing-4) * 2 / 3));
    --testimonial-card-gap-width: var(--spacing-4);

    padding: var(--spacing-1) var(--spacing-6);
  }

  .testimonial__list {
    min-height: 480px;
  }
}
</style>
